import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Radio, RadioGroup } from '@ordr-as/umami-react/radio';
import { CartAdditionOption } from '../../../../../types/cart';
import { isEqualNotOptionId, isEqualOptionId } from '../../../../utils/cart';
import { useLocation } from '../../../../context/LocationContext';
import { OptionGroupDto } from '../../../../../generated-api';
import { useCheckout } from '../../../../context/CheckoutContext';

const findDefaultOption = (optionGroup: OptionGroupDto, defaultOptions: CartAdditionOption[]) => {
  const selectedOption = optionGroup.options.find((itemOption) => defaultOptions.some(isEqualOptionId(itemOption)));
  return selectedOption ? selectedOption : optionGroup.options.find((itemOption) => itemOption.preSelected);
};

const findDefaultValue = (optionGroup: OptionGroupDto, defaultOptions: CartAdditionOption[]) => {
  const selectedOption = optionGroup.options.findIndex((itemOption) =>
    defaultOptions.some(isEqualOptionId(itemOption))
  );

  if (selectedOption > -1) {
    return selectedOption.toString();
  }

  return optionGroup.options.findIndex((itemOption) => itemOption.preSelected).toString();
};

interface Props {
  optionGroup: OptionGroupDto;
  setOptions: Dispatch<SetStateAction<CartAdditionOption[]>>;
  defaultOptions: CartAdditionOption[];
}

const SingleChoiceSelection: FC<Props> = ({ optionGroup, setOptions, defaultOptions }) => {
  const { formatCurrency } = useLocation();

  const [selectedOption, setSelectedOption] = useState(findDefaultOption(optionGroup, defaultOptions));
  const { vatType } = useCheckout();

  useEffect(() => {
    if (selectedOption) {
      setOptions((cartOptions) =>
        cartOptions
          .filter(isEqualNotOptionId(selectedOption))
          .concat({ quantity: 1, option: selectedOption, type: optionGroup.productType } as CartAdditionOption)
      );
    }
  }, [optionGroup.productType, selectedOption, setOptions]);

  const handleSelection = useCallback(
    (index: string) => {
      const option = optionGroup.options[parseInt(index)]!;
      setOptions((cartOptions) =>
        cartOptions
          .filter(isEqualNotOptionId(selectedOption))
          .concat({ quantity: 1, option, type: optionGroup.productType, vatType: vatType })
      );
      setSelectedOption(option);
    },
    [optionGroup.options, optionGroup.productType, setOptions, selectedOption, vatType]
  );

  return (
    <RadioGroup
      className="divide-y divide-grey-medium"
      aria-label="Select Option"
      onChange={handleSelection}
      defaultValue={findDefaultValue(optionGroup, defaultOptions)}
    >
      {optionGroup.options.map(({ optionId, name, price, takeawayPrice }, index) => (
        <Radio className="py-4" key={optionId} value={index.toString()}>
          {name}
          {price > 0 && (
            <span className="ml-auto">
              + {takeawayPrice ? `${formatCurrency(price)} | ${formatCurrency(takeawayPrice)}` : formatCurrency(price)}
            </span>
          )}
        </Radio>
      ))}
    </RadioGroup>
  );
};

export default SingleChoiceSelection;
